import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rolesAPI, groupsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import DeleteButton from '../components/ui/DeleteButton';
import { Plus, Pencil, Trash2, ArrowLeft, Check, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { canAdmin } from '../utils/permissions';

const RoleManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [editingGroupName, setEditingGroupName] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleNameDraft, setRoleNameDraft] = useState('');
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newRoleName, setNewRoleName] = useState('');

  const isAdmin = canAdmin(user);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [rolesRes, groupsRes] = await Promise.all([rolesAPI.getAll(), groupsAPI.getAll()]);
      setRoles(rolesRes.data);
      setGroups(groupsRes.data);
      if (!selectedGroupId && groupsRes.data.length > 0) {
        setSelectedGroupId(groupsRes.data[0].group_id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const selectedGroup = groups.find((g) => g.group_id === selectedGroupId);
  const filteredRoles = selectedGroup ? roles.filter((r) => r.group === selectedGroup.name) : [];

  const startEditGroup = () => {
    if (!selectedGroup) return;
    setGroupNameDraft(selectedGroup.name);
    setEditingGroupName(true);
  };

  const saveGroupName = async () => {
    if (!groupNameDraft.trim() || !selectedGroup) return;
    try {
      await groupsAPI.update(selectedGroup.group_id, { name: groupNameDraft.trim() });
      toast.success('Group renamed');
      setEditingGroupName(false);
      loadAll();
    } catch (error) {
      toast.error('Failed to rename group');
    }
  };

  const startEditRole = (role) => {
    setEditingRoleId(role.role_id);
    setRoleNameDraft(role.name);
  };

  const saveRoleName = async (role) => {
    if (!roleNameDraft.trim()) return;
    try {
      await rolesAPI.update(role.role_id, { name: roleNameDraft.trim(), group: role.group });
      toast.success('Role renamed');
      setEditingRoleId(null);
      loadAll();
    } catch (error) {
      toast.error('Failed to rename role');
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      const res = await groupsAPI.create({ name: newGroupName.trim() });
      toast.success('Group added');
      setIsAddGroupOpen(false);
      setNewGroupName('');
      await loadAll();
      setSelectedGroupId(res.data.group_id);
    } catch (error) {
      toast.error('Failed to add group');
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim() || !selectedGroup) return;
    try {
      await rolesAPI.create({ name: newRoleName.trim(), group: selectedGroup.name });
      toast.success('Role added');
      setIsAddRoleOpen(false);
      setNewRoleName('');
      loadAll();
    } catch (error) {
      toast.error('Failed to add role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await rolesAPI.delete(roleId);
      toast.success('Role deleted');
      loadAll();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    const groupRoleCount = filteredRoles.length;
    const msg = groupRoleCount > 0
      ? `This group has ${groupRoleCount} role(s). Delete group and all its roles?`
      : 'Delete this group?';
    if (!window.confirm(msg)) return;
    try {
      for (const role of filteredRoles) {
        await rolesAPI.delete(role.role_id);
      }
      await groupsAPI.delete(selectedGroup.group_id);
      toast.success('Group deleted');
      setSelectedGroupId('');
      loadAll();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(filteredRoles);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // Optimistic update
    setRoles(prev => [
      ...prev.filter(r => r.group !== selectedGroup.name),
      ...reordered,
    ]);
    try {
      await rolesAPI.reorder(reordered.map(r => r.role_id));
    } catch {
      toast.error('Failed to save order');
      loadAll();
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50">
        <Navbar />
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Header */}
        <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-8">
          <button
            onClick={() => navigate('/people')}
            data-testid="back-to-people-btn"
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors mt-0.5"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900">Role Management</h1>
            <p className="mt-1 text-sm sm:text-base text-slate-600">Manage groups and roles</p>
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-12 sm:py-16 bg-white border border-slate-200 rounded-xl">
            <p className="text-sm text-slate-500 mb-4">No groups yet. Create your first group to get started.</p>
            {isAdmin && (
              <button onClick={() => setIsAddGroupOpen(true)} className="text-sm text-green-700 hover:text-green-800 font-medium">
                Add your first group
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Add Group + Group Selector */}
            <div className="mb-4 sm:mb-6">
              {isAdmin && (
                <button
                  onClick={() => setIsAddGroupOpen(true)}
                  data-testid="add-group-btn"
                  className="h-9 sm:h-10 px-4 sm:px-6 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm mb-3"
                >
                  <Plus className="w-4 h-4" />
                  Add Group
                </button>
              )}
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Group</Label>
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger data-testid="group-selector" className="w-full sm:max-w-sm bg-white">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {groups.map((g) => (
                    <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedGroup && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                {/* Group Header */}
                <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100">
                  {/* Group name row */}
                  <div className="flex items-center justify-between mb-3 sm:mb-0">
                    {editingGroupName ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={groupNameDraft}
                          onChange={(e) => setGroupNameDraft(e.target.value)}
                          data-testid="group-name-edit-input"
                          className="text-base sm:text-lg font-semibold flex-1"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') saveGroupName(); if (e.key === 'Escape') setEditingGroupName(false); }}
                        />
                        <button onClick={saveGroupName} data-testid="save-group-name-btn" className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingGroupName(false)} data-testid="cancel-group-name-btn" className="p-2 text-slate-400 hover:bg-slate-50 rounded-lg transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg sm:text-xl font-semibold text-slate-900">{selectedGroup.name}</h2>
                        {isAdmin && (
                          <button onClick={startEditGroup} data-testid="rename-group-btn" className="p-1.5 text-slate-400 hover:text-green-700 hover:bg-slate-50 rounded-lg transition-colors">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Action buttons */}
                  {isAdmin && !editingGroupName && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setIsAddRoleOpen(true)}
                        data-testid="add-role-btn"
                        className="h-8 sm:h-9 px-3 sm:px-4 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 text-xs sm:text-sm font-medium flex items-center gap-1.5"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Role
                      </button>
                      <DeleteButton
                        onConfirm={handleDeleteGroup}
                        className="h-8 sm:h-9 px-3 sm:px-4 rounded-full border border-rose-200 text-xs sm:text-sm font-medium flex items-center gap-1.5"
                        iconSize="w-3.5 h-3.5"
                        title="Delete group"
                        testId="delete-group-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete Group</span>
                      </DeleteButton>
                    </div>
                  )}
                </div>

                {/* Roles List */}
                <div className="p-4 sm:p-6">
                  {filteredRoles.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6 sm:py-8">No roles in this group yet.</p>
                  ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="roles-list" isDropDisabled={!isAdmin}>
                        {(provided) => (
                          <div
                            className="space-y-2"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {filteredRoles.map((role, index) => (
                              <Draggable
                                key={role.role_id}
                                draggableId={role.role_id}
                                index={index}
                                isDragDisabled={!isAdmin || editingRoleId === role.role_id}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    data-testid={`role-item-${role.role_id}`}
                                    className={`flex items-center justify-between px-3 sm:px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg hover:bg-slate-100 transition-colors ${snapshot.isDragging ? 'shadow-lg border-green-200 bg-white' : ''}`}
                                  >
                                    {/* Drag handle */}
                                    {isAdmin && (
                                      <div
                                        {...provided.dragHandleProps}
                                        className="mr-2 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing flex-shrink-0"
                                        data-testid={`drag-handle-${role.role_id}`}
                                      >
                                        <GripVertical className="w-4 h-4" />
                                      </div>
                                    )}
                                    {editingRoleId === role.role_id ? (
                                      <div className="flex items-center gap-2 flex-1">
                                        <Input
                                          value={roleNameDraft}
                                          onChange={(e) => setRoleNameDraft(e.target.value)}
                                          data-testid="role-name-edit-input"
                                          className="h-8 text-sm flex-1"
                                          autoFocus
                                          onKeyDown={(e) => { if (e.key === 'Enter') saveRoleName(role); if (e.key === 'Escape') setEditingRoleId(null); }}
                                        />
                                        <button onClick={() => saveRoleName(role)} data-testid="save-role-name-btn" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setEditingRoleId(null)} data-testid="cancel-role-name-btn" className="p-1.5 text-slate-400 hover:bg-slate-50 rounded transition-colors">
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-sm font-medium text-slate-700 flex-1">{role.name}</span>
                                        {isAdmin && (
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => startEditRole(role)}
                                              data-testid={`rename-role-${role.role_id}`}
                                              className="p-1.5 text-slate-400 hover:text-green-700 hover:bg-white rounded transition-colors"
                                            >
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <DeleteButton
                                              onConfirm={() => handleDeleteRole(role.role_id)}
                                              className="p-1.5 rounded"
                                              iconSize="w-3.5 h-3.5"
                                              title="Delete role"
                                              testId={`delete-role-${role.role_id}`}
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Group Dialog */}
        <Dialog open={isAddGroupOpen} onOpenChange={setIsAddGroupOpen}>
          <DialogContent className="bg-white mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Add Group</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddGroup} className="space-y-4">
              <div>
                <Label htmlFor="new-group-name">Group Name</Label>
                <Input
                  id="new-group-name"
                  data-testid="new-group-name-input"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., Media Team"
                  required
                />
              </div>
              <Button type="submit" data-testid="submit-group-btn" className="w-full">Add Group</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Role Dialog */}
        <Dialog open={isAddRoleOpen} onOpenChange={setIsAddRoleOpen}>
          <DialogContent className="bg-white mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Add Role to {selectedGroup?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddRole} className="space-y-4">
              <div>
                <Label htmlFor="new-role-name">Role Name</Label>
                <Input
                  id="new-role-name"
                  data-testid="new-role-name-input"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Sound Engineer"
                  required
                />
              </div>
              <Button type="submit" data-testid="submit-role-btn" className="w-full">Add Role</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RoleManagement;
